import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  query getReading($filter: readingFilterInput) {
    getReading(filter: $filter) {
      bpReading {
        meanReferenceRange
        meanValue
        meanColor
        meanFlag
        diastolicReferenceRange
        diastolicValue
        diastolicColor
        diastolicFlag
        systolicReferenceRange
        systolicValue
        systolicColor
        systolicFlag
      }
      glucometerReading {
        ppReferenceRange
        ppValue
        ppColor
        ppFlag
        fastingReferenceRange
        fastingValue
        fastingColor
        fastingFlag
        randomReferenceRange
        randomValue
        randomColor
        randomFlag
      }
      weighingReading {
        bmiReferenceRange
        bmiValue
        bmiColor
        bmiFlag
        weightValue
      }
      ecgReading {
        ecgUrl
        pdfUrl
      }
      ishReading {
        referenceRange
        color
        score
        readingId
      }
      ewsReading {
        uoColor
        uoScore
        uoValue
        avpuColor
        avpuScore
        avpuValue
        systolicColor
        systolicScore
        systolicValue
        rrColor
        rrScore
        rrValue
        o2Color
        o2Score
        o2Value
        skinColor
        skinScore
        skinValue
        pulseColor
        pulseScore
        pulseValue
      }
      readingRisk {
        riskCount
        covidRisk
        respiratoryRisk
        obesityRisk
        cardiacRisk
        diabeticRisk
        hypotensionRisk
        hypertensionRisk
        id
      }
      id
      datetime
      type
      doctorViewedDatetime
      location
      vincenseReading {
        rrReferenceRange
        rrValue
        rrColor
        rrFlag
        o2ReferenceRange
        o2Value
        o2Color
        o2Flag
        pulseReferenceRange
        pulseValue
        pulseColor
        pulseFlag
        skinValue
      }
      subject {
        organization {
          id
          name
          shortName
          logoUrl
          level {
            id
            name
          }
        }
        mobile
        photo
        bloodGroup
        dob
        gender
        name
        groupId
        subjectType
        id
      }
      viewedByDoctor {
        code
        name
        id
      }
      customTestList {
        customTest {
          id
          name
          type
          customTestGroup {
            id
            name
          }
        }
        value
        id
      }
      additionalReportList {
        submittedDatetime
        fileUrl
        reportName
      }
      error {
        statusCode
        message
      }
    }
  }
`;

const initialState: TYPES.ReadingState = {
  result: null,
  error: null,
  loading: false
};

export const getReadingRequest = createAsyncThunk<
  TYPES.Reading,
  {filter?: TYPES.ReadingFilterInput;},
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'reading/get_reading',
  async (params: {filter?: TYPES.ReadingFilterInput;}, { extra: client }) => {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params
      });
      return response.data.getReading;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const getReadingSlice = createSlice({
  name: 'get_reading',
  initialState: initialState,
  reducers: {
    getReadingClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getReadingRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(getReadingRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(getReadingRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { getReadingClear } = getReadingSlice.actions;

export const getReading = createSelector(
  (state: RootState) => state.reading.getReading.result,
  (state: RootState) => state.reading.getReading.error,
  (state: RootState) => state.reading.getReading.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getReadingSlice.reducer;